import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "../../Components/Button";
import { DeleteCourse, GetAllCoursesAdmin } from "../../DataService/CourseService";
import { Course, CourseStatus, COURSE_STATUS_OPTIONS, DEFAULT_COURSE_CATEGORY, isStandaloneCourseCategory } from "../../Types/CourseTypes";
import { CourseProductCardDto, GetProductCardByCourseId, SyncAllProductCardsStripe } from "../../DataService/ProductCardService";
import { AdminCourseEditor } from "./AdminCourseEditor";
import "./AdminCoursesV2.css";

const getCourseStatusLabel = (status: CourseStatus) => {
  switch (status) {
    case CourseStatus.Published:
      return "Publicerad";
    case CourseStatus.Archived:
      return "Arkiverad";
    default:
      return "Utkast";
  }
};

const getCourseStatusTone = (status: CourseStatus) => {
  switch (status) {
    case CourseStatus.Published:
      return "published";
    case CourseStatus.Archived:
      return "archived";
    default:
      return "draft";
  }
};

const getStripeLabel = (course: Course, card?: CourseProductCardDto) => {
  if (!course.isStandalone) return "Intern";
  if (!card?.stripeProductId) return "Inte synkad";
  return card.stripePriceId ? "Synkad" : "Delvis synkad";
};

const getCategoryLabel = (category?: string | null) => {
  const normalized = String(category ?? DEFAULT_COURSE_CATEGORY).trim().toLowerCase();
  if (normalized === "__system-membership") return "GROW";
  if (normalized === "__system-rot") return "ROT";
  return "Marketplace";
};

const emptyDraftCourse = (): Course => ({
  id: 0,
  title: "",
  description: "",
  status: CourseStatus.Draft,
  category: DEFAULT_COURSE_CATEGORY,
  isStandalone: isStandaloneCourseCategory(DEFAULT_COURSE_CATEGORY),
  createdAt: "",
  updatedAt: "",
  chapters: []
});

const AdminCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [productCards, setProductCards] = useState<Record<number, CourseProductCardDto>>({});
  const [activeCourse, setActiveCourse] = useState<Course | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isSyncingAllStripe, setIsSyncingAllStripe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadCourses = async () => {
    setIsLoading(true);
    try {
      const courseList = await GetAllCoursesAdmin();

      if (courseList) setCourses(courseList);

       const cards = await Promise.all(
         (courseList ?? [])
           .filter((course) => course.isStandalone)
           .map(async (course) => {
            try {
              const card = await GetProductCardByCourseId(course.id);
              return card ? [course.id, card] as const : null;
            } catch {
              return null;
            }
          })
      );

      setProductCards(Object.fromEntries(cards.filter(Boolean) as Array<readonly [number, CourseProductCardDto]>));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCourse = async (course: Course) => {
    const confirmed = window.confirm(`Ta bort kursen "${course.title}"?`);
    if (!confirmed) return;

    const deleted = await DeleteCourse(course.id);
    if (!deleted) return;

    setCourses((prev) => prev.filter((c) => c.id !== course.id));
    setProductCards((prev) => {
      const next = { ...prev };
      delete next[course.id];
      return next;
    });

    if (activeCourse?.id === course.id) {
      setActiveCourse(null);
    }
  };

  const syncAllStripe = async () => {
    try {
      setIsSyncingAllStripe(true);
      const synced = await SyncAllProductCardsStripe();
      toast.success(`Synkade ${synced?.length ?? 0} kursprodukter till Stripe`);
    } catch {
      toast.error("Kunde inte synka alla kursprodukter till Stripe");
    } finally {
      setIsSyncingAllStripe(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const title = course.title ?? "";
      const description = course.description ?? "";
      const matchesSearch =
        search.trim() === "" ||
        title.toLowerCase().includes(search.toLowerCase()) ||
        description.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || course.status === Number(statusFilter);
      return matchesSearch && matchesStatus;
    });
  }, [courses, search, statusFilter]);

  const publishedCount = courses.filter((c) => c.status === CourseStatus.Published).length;
  const draftCount = courses.filter((c) => c.status === CourseStatus.Draft).length;
  const archivedCount = courses.filter((c) => c.status === CourseStatus.Archived).length;
  const totalChapters = courses.reduce((t, c) => t + c.chapters.length, 0);
  const totalUsers = courses.reduce((t, c) => t + (c.enrolledUsers ?? 0), 0);
  const syncedCount = courses.filter((course) => productCards[course.id]?.stripeProductId).length;
  const selectedFilterLabel = statusFilter === "all"
    ? "Alla statusar"
    : COURSE_STATUS_OPTIONS.find((opt) => String(opt.value) === statusFilter)?.label ?? "Filter";

  if (activeCourse) {
    return (
      <AdminCourseEditor
        course={activeCourse}
        onBack={() => setActiveCourse(null)}
        onSaved={(savedCourse) => {
          setCourses((prev) => {
            const exists = prev.some((c) => c.id === savedCourse.id);
            return exists
              ? prev.map((c) => (c.id === savedCourse.id ? savedCourse : c))
              : [...prev, savedCourse];
          });
          setActiveCourse(savedCourse);
        }}
      />
    );
  }

  return (
    <div className="ac-v2">
      <div className="ac-v2-main-layout">
        <section className="ac-v2-hero admin-surface-card">
          <div className="ac-v2-hero-copy">
            <span className="ac-v2-kicker">Kurshantering</span>
            <h1 className="admin-page-title">Kurser och innehåll</h1>
            <p className="admin-page-subtitle">Skapa, redigera och publicera kurser utan att tappa kontroll på Stripe, kapitel och status.</p>
          </div>

          <div className="ac-v2-hero-actions">
            <Button
              text={isSyncingAllStripe ? "Synkar Stripe..." : "Synka Stripe"}
              variant="secondary"
              onClick={() => void syncAllStripe()}
              disabled={isSyncingAllStripe}
            />
            <button className="ac-v2-primary-cta" onClick={() => setActiveCourse(emptyDraftCourse())}>
              + Skapa ny kurs
            </button>
          </div>
        </section>

        <section className="ac-v2-stats">
          <div className="ac-v2-stat-card">
            <span className="ac-v2-stat-label">Totala kurser</span>
            <span className="ac-v2-stat-value">{courses.length}</span>
          </div>

          <div className="ac-v2-stat-card">
            <span className="ac-v2-stat-label">Publicerade</span>
            <span className="ac-v2-stat-value">{publishedCount}</span>
          </div>

          <div className="ac-v2-stat-card">
            <span className="ac-v2-stat-label">Kapitel</span>
            <span className="ac-v2-stat-value">{totalChapters}</span>
          </div>

          <div className="ac-v2-stat-card">
            <span className="ac-v2-stat-label">Användare</span>
            <span className="ac-v2-stat-value">{totalUsers}</span>
          </div>
        </section>

        <section className="ac-v2-filter-bar admin-surface-card">
          <div className="ac-v2-search-wrap">
            <label className="ac-v2-filter-label" htmlFor="course-search">Sök katalog</label>
            <input
              id="course-search"
              type="text"
              placeholder="Sök kurs, beskrivning eller innehåll..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ac-v2-search"
            />
          </div>

          <div className="ac-v2-filter-actions">
            <label className="ac-v2-filter-label" htmlFor="course-status-filter">Status</label>
            <select
              id="course-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="ac-v2-status-filter"
            >
              <option value="all">Alla statusar</option>
              {COURSE_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="ac-v2-filter-summary">Visar {filteredCourses.length} av {courses.length} kurs{courses.length === 1 ? "" : "er"} · {selectedFilterLabel}</span>
          </div>
        </section>

        <div className="ac-v2-main-grid">
          <section className="ac-v2-course-list">
            {isLoading ? (
              <div className="ac-v2-empty-state">
                <span className="ac-v2-empty-state__title">Laddar kurser...</span>
                <p>Hämtar kurskatalog, produktkort och Stripe-status.</p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="ac-v2-empty-state">
                <span className="ac-v2-empty-state__title">Inga kurser matchar filtret</span>
                <p>Prova att ändra status eller rensa sökningen för att hitta rätt kurs.</p>
                <Button text="Skapa ny kurs" onClick={() => setActiveCourse(emptyDraftCourse())} />
              </div>
            ) : (
              filteredCourses.map((course) => {
                const productCard = productCards[course.id];
                const stripeLabel = getStripeLabel(course, productCard);

                return (
                  <div key={course.id} className="ac-v2-course-row">
                    <div className="ac-v2-course-image">
                      {productCard?.bannerImageUrl ? (
                        <img src={productCard.bannerImageUrl} alt={course.title} />
                      ) : (
                        <div className="ac-v2-course-image-placeholder">
                          <span>{course.title ? course.title.slice(0, 2).toUpperCase() : "KR"}</span>
                        </div>
                      )}

                      
                    </div>

                    <div className="ac-v2-course-content">
                      <div className="ac-v2-course-header">
                        <div>
                          <h3>{course.title}</h3>
                          <p className="ac-v2-course-description">{course.description}</p>
                        </div>
                        <span className="ac-v2-course-price">{productCard?.price ?? "—"}</span>
                      </div>

                      <div className="ac-v2-course-meta ac-v2-course-meta--panel">
                        <span>{course.chapters.length} kapitel</span>
                        <span>{course.enrolledUsers ?? 0} användare</span>
                        <span>{course.totalDurationSeconds ? `${Math.round(course.totalDurationSeconds / 60)} min` : "Ingen tid satt"}</span>
                        {productCard?.stripePriceId && <span className="ac-v2-mono">Price: {productCard.stripePriceId}</span>}
                      </div>

                      <div className="ac-v2-course-actions">
                        <div className="ac-v2-course-actions-left">
                          <Button text="Öppna" size="sm" onClick={() => setActiveCourse(course)} />
                          <button className="ac-v2-secondary-link" onClick={() => setActiveCourse(course)} type="button">
                            Redigera innehåll
                          </button>
                          <Button
                            text="Ta bort"
                            size="sm"
                            variant="danger"
                            onClick={() => void handleDeleteCourse(course)}
                          />
                        </div>
                        <div className="ac-v2-course-inline-tags">
                          <span className={`ac-v2-course-status ${getCourseStatusTone(course.status)}`}>
                            {getCourseStatusLabel(course.status)}
                          </span>
                          <span className="ac-v2-course-category">{getCategoryLabel(course.category)}</span>
                          <span className={`ac-v2-course-sync ${productCard?.stripeProductId ? "synced" : "pending"}`}>
                            Stripe: {stripeLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </section>

          <aside className="ac-v2-side-panel">
            <div className="ac-v2-help-card">
              <h3>System guide</h3>
              <p>För publicering behöver kursen vara sparad, relevant Stripe-data vara synkad och kursinnehållet kännas färdigt.</p>

              <div className="ac-v2-guide-list">
                <div>
                  <span>1</span>
                  <p>Skapa eller öppna kursen</p>
                </div>
                <div>
                  <span>2</span>
                  <p>Uppdatera grundinfo och produktkort</p>
                </div>
                <div>
                  <span>3</span>
                  <p>Lägg till kapitel och synka Stripe vid behov</p>
                </div>
              </div>

              <button className="ac-v2-help-link" type="button" onClick={() => setActiveCourse(emptyDraftCourse())}>
                Skapa ny kurs
              </button>
            </div>

            <div className="ac-v2-tips-card">
              <h4>Snabb läsning</h4>
              <p>{publishedCount} publicerade, {draftCount} utkast och {archivedCount} arkiverade kurser. {syncedCount} kurser har Stripe-synk.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AdminCourses;
